import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { CheckCircle2, XCircle, Loader2, AlertTriangle } from 'lucide-react'
import { getPaymentStatusOptions } from '@/lib/client/@tanstack/react-query.gen'

export const Route = createFileRoute('/link/$parentId/payment-return-checkout')({
  component: PaymentReturnCheckout,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      paymentId: search.paymentId as string | undefined,
      checkoutIntentId: search.checkoutIntentId ? Number(search.checkoutIntentId) : undefined,
      code: search.code as string | undefined,
      type: search.type as string | undefined,
      error: search.error as string | undefined,
    }
  },
})

function PaymentReturnCheckout() {
  const { parentId } = Route.useParams()
  const navigate = useNavigate()
  const searchParams = Route.useSearch()
  const [pollingEnabled, setPollingEnabled] = useState(true)
  const [paymentState, setPaymentState] = useState<'checking' | 'success' | 'error' | 'pending'>('checking')

  const { data: checkoutStatus, isLoading } = useQuery({
    ...getPaymentStatusOptions({
      path: {
        parentId,
        paymentId: searchParams.paymentId!,
      },
    }),
    enabled: pollingEnabled && !!searchParams.paymentId,
    refetchInterval: (query) => {
      if (!pollingEnabled) return false

      const data = query.state.data
      if (!data) return 2000

      const order = data.order
      if (!order || !order.payments || order.payments.length === 0) {
        return 2000
      }

      const hasSuccessPayment = order.payments.some((p: any) => p.state === 'Authorized')
      const hasFailedPayment = order.payments.some((p: any) =>
        p.state === 'Refused' || p.state === 'Contested'
      )

      if (hasSuccessPayment || hasFailedPayment) {
        setPollingEnabled(false)
        return false
      }

      return 2000
    },
  })

  useEffect(() => {
    if (searchParams.type === 'error') {
      setPaymentState('error')
      setPollingEnabled(false)
      return
    }

    if (!checkoutStatus) return

    const order = checkoutStatus.order
    if (!order || !order.payments || order.payments.length === 0) {
      setPaymentState('pending')
      return
    }

    const hasSuccessPayment = order.payments.some((p: any) => p.state === 'Authorized')
    const hasFailedPayment = order.payments.some((p: any) =>
      p.state === 'Refused' || p.state === 'Contested'
    )

    if (hasSuccessPayment) {
      setPaymentState('success')
      setTimeout(() => {
        navigate({ to: '/link/$parentId', params: { parentId } })
      }, 3000)
    } else if (hasFailedPayment) {
      setPaymentState('error')
    } else {
      setPaymentState('pending')
    }
  }, [checkoutStatus, searchParams.type, navigate, parentId])

  const renderContent = () => {
    if (searchParams.type === 'error') {
      return (
        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <div className="flex items-center space-x-3">
              <XCircle className="h-12 w-12 text-red-500" />
              <div>
                <CardTitle className="text-2xl">Erreur technique</CardTitle>
                <CardDescription>Une erreur est survenue lors du traitement du paiement</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {searchParams.error
                  ? `Erreur : ${searchParams.error}`
                  : 'Une erreur technique est survenue pendant le paiement. Veuillez réessayer ou contacter le support.'}
              </AlertDescription>
            </Alert>
            <div className="flex space-x-3">
              <Button
                onClick={() => navigate({ to: '/link/$parentId/pay', params: { parentId } })}
                className="flex-1"
              >
                Réessayer
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate({ to: '/link/$parentId', params: { parentId } })}
                className="flex-1"
              >
                Retour au tableau de bord
              </Button>
            </div>
          </CardContent>
        </Card>
      )
    }

    if (isLoading || paymentState === 'checking' || paymentState === 'pending') {
      return (
        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <div className="flex items-center space-x-3">
              <Loader2 className="h-12 w-12 text-blue-500 animate-spin" />
              <div>
                <CardTitle className="text-2xl">Vérification du paiement</CardTitle>
                <CardDescription>Nous vérifions le statut de votre paiement...</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Alert>
              <AlertDescription>
                Cette opération peut prendre quelques instants. Merci de patienter.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      )
    }

    if (paymentState === 'success') {
      return (
        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <div className="flex items-center space-x-3">
              <CheckCircle2 className="h-12 w-12 text-green-500" />
              <div>
                <CardTitle className="text-2xl">Paiement réussi</CardTitle>
                <CardDescription>Votre paiement a été traité avec succès</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>
                Merci pour votre paiement. Vous allez être redirigé vers votre tableau de bord dans quelques instants.
              </AlertDescription>
            </Alert>
            {checkoutStatus?.order && (
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Montant payé :</span>
                  <span className="font-semibold">
                    {(checkoutStatus.order.amount.total / 100).toFixed(2)}€
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Date :</span>
                  <span className="font-semibold">
                    {new Date(checkoutStatus.order.date).toLocaleString('fr-FR')}
                  </span>
                </div>
                {checkoutStatus.order.payments?.[0]?.id && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Référence :</span>
                    <span className="font-mono text-xs">
                      {checkoutStatus.order.payments[0].id}
                    </span>
                  </div>
                )}
              </div>
            )}
            <Button
              onClick={() => navigate({ to: '/link/$parentId', params: { parentId } })}
              className="w-full"
            >
              Retour au tableau de bord
            </Button>
          </CardContent>
        </Card>
      )
    }

    if (paymentState === 'error') {
      return (
        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <div className="flex items-center space-x-3">
              <XCircle className="h-12 w-12 text-red-500" />
              <div>
                <CardTitle className="text-2xl">Paiement refusé</CardTitle>
                <CardDescription>Votre paiement n'a pas pu être traité</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Le paiement a été refusé par votre banque ou a été contesté.
                Veuillez vérifier vos informations bancaires et réessayer.
              </AlertDescription>
            </Alert>
            <div className="flex space-x-3">
              <Button
                onClick={() => navigate({ to: '/link/$parentId/pay', params: { parentId } })}
                className="flex-1"
              >
                Réessayer
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate({ to: '/link/$parentId', params: { parentId } })}
                className="flex-1"
              >
                Retour au tableau de bord
              </Button>
            </div>
          </CardContent>
        </Card>
      )
    }

    return null
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      {renderContent()}
    </div>
  )
}
